import { Badge, Box, Col, Row, Table, Text } from '@ksyos/design-system';
import { useMemo } from 'react';
import type { IProcessQuestionnaireResponseItem } from '@common/interfaces/api/processQuestionnaireResponsesApi';
import { essQuestionnairePrefixMappingToKeys } from '@common/utils/osasQuestionnaireItemsMappingUtils';
import { getSelectedLanguage } from '@frontend/translate';

interface IBadgeQuestionnaireResponseProps {
    questionnaireResponseItems: IProcessQuestionnaireResponseItem[];
}

export function BadgeQuestionnaireResponse(props: Readonly<IBadgeQuestionnaireResponseProps>) {
    const { questionnaireResponseItems } = props;

    const translate = getSelectedLanguage();

    const { leftColumnItems, rightColumnItems } = useMemo(() => {
        const leftColumn: JSX.Element[] = [];
        const rightColumn: JSX.Element[] = [];

        const sortedQuestionnaireResponseItems = questionnaireResponseItems.sort(
            (a, b) => (b.answer.itemWeight || 0) - (a.answer.itemWeight || 0)
        );

        for (const item of sortedQuestionnaireResponseItems) {
            const itemWeight = item.answer.itemWeight ?? 0;
            const itemmText =
                translate.osasQuestionnaires[
                    essQuestionnairePrefixMappingToKeys[
                        item.prefix as keyof typeof essQuestionnairePrefixMappingToKeys
                    ] as keyof typeof translate.osasQuestionnaires
                ];
            const component = (
                <Box
                    alignItems={'center'}
                    display={'flex'}
                    flexDirection={'row'}
                    gap={4}
                    justifyContent={'space-between'}
                    key={item.prefix}
                    marginTop={4}
                    paddingRight={4}
                    width={'100%'}
                >
                    <Text size="sm">{itemmText}</Text>
                    <Badge
                        color={getBadgeColor(itemWeight)}
                        shape="circle"
                        size="small"
                        text={String(itemWeight)}
                    />
                </Box>
            );

            if (itemWeight >= 2) {
                leftColumn.push(component);
            } else {
                rightColumn.push(component);
            }
        }

        return { leftColumnItems: leftColumn, rightColumnItems: rightColumn };
    }, [questionnaireResponseItems]);

    return (
        <Table>
            <Row>
                <Col span={[12, 6]}>{leftColumnItems}</Col>
                <Col span={[12, 6]}>{rightColumnItems}</Col>
            </Row>
        </Table>
    );

    function getBadgeColor(value: number) {
        switch (value) {
            case 0:
                return 'green';
            case 1:
                return 'yellow';
            case 2:
                return 'orange';
            case 3:
                return 'red';
            default:
                break;
        }
    }
}
